import { AffiliateAttribution, AffiliateListItem } from '@models/affiliate';
import { AdminUser, PartnerProfile, UserProfile } from '@models/auth';
import { PagenatedResponse, SearchPagenateQuery } from '@models/common';
import { AdminCreditPointLog } from '@models/credit-points';
import { JoinPromotionLog } from '@models/join-promotion';
import { AdminUserFavorite } from '@models/user-favorite';
import { apiClient } from '.';

export const searchUsers = async (query?: SearchPagenateQuery) => {
  return apiClient.get<PagenatedResponse<AdminUser>>(`/admin/v1/users`, {
    params: query,
  });
};

export const getUser = async (userId: string) => {
  return apiClient.get<AdminUser>(`/admin/v1/users/${userId}`);
};

export const updateUserProfile = async (data: { userId: string; body: Partial<UserProfile> }) => {
  const userId = data.userId;
  return apiClient.put<AdminUser>(`/admin/v1/users/${userId}/profile`, data.body);
};

export const setExcellentPartner = async (props: { userId: string; selected: boolean }) => {
  return apiClient.patch(`/admin/v1/users/${props.userId}/excellent-partner`, { selected: props.selected });
};

export const runExcellentPartnerSelection = async () => {
  return apiClient.post<{ result: 'OK' | 'ALREADY_RUNNING' }>('/admin/v1/excellent-partners/run');
};

export const updatePartnerProfile = async (props: { userId: string; body: PartnerProfile }) => {
  return apiClient.patch<AdminUser>(`/admin/v1/users/${props.userId}/partner-profile`, props.body);
};

export const getUserCreditPointLogs = async (userId: string) => {
  return apiClient.get<AdminCreditPointLog[]>(`/admin/v1/users/${userId}/credit-point-logs`);
};

export const getUserJoinPromotionLogs = async (userId: string, query?: { page?: number; size?: number }) => {
  return apiClient.get<PagenatedResponse<JoinPromotionLog>>(`/admin/v1/users/${userId}/join-promotion-logs`, {
    params: query,
  });
};

export const getUserFavorites = async (userId: string, query?: { page?: number; size?: number; type?: string }) => {
  return apiClient.get<PagenatedResponse<AdminUserFavorite>>(`/admin/v1/users/${userId}/favorites`, {
    params: query,
  });
};

export const getUserAssessmentChildNames = async (userId: string) => {
  return apiClient.get<{ childNames: string[] }>(`/admin/v1/users/${userId}/assessment-child-names`);
};

export const getUserAffiliates = async (
  userId: string,
  query?: {
    page?: number;
    size?: number;
    targetType?: 'product' | 'campaign';
    sortBy?: 'createdAt' | 'visitCount' | 'paymentCount' | 'totalPaymentAmount';
    sortOrder?: 'asc' | 'desc';
  },
) => {
  return apiClient.get<PagenatedResponse<AffiliateListItem>>(`/admin/v1/users/${userId}/affiliates`, {
    params: query,
  });
};

export const getUserAffiliateAttributions = async (
  userId: string,
  query?: { page?: number; size?: number; attributionType?: string },
) => {
  return apiClient.get<PagenatedResponse<AffiliateAttribution>>(`/admin/v1/users/${userId}/affiliate-attributions`, {
    params: query,
  });
};

export const updateUserPoint = async (body: {
  userId: string;
  amount: number;
  content: string;
  type: 'add' | 'subtract';
}) => {
  const userId = body.userId;
  return apiClient.put<AdminUser>(`/admin/v1/users/${userId}/point`, body);
};

export const createUser = async (formData: FormData) => {
  return apiClient.post<AdminUser>(`/admin/v1/users`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const updateUser = async (formData: FormData) => {
  const userId = formData.get('userId');
  return apiClient.put<AdminUser>(`/admin/v1/users/${userId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const getSearchUserssExcelFile = async (query?: SearchPagenateQuery) => {
  return apiClient.get(`/admin/v1/users/export`, {
    params: query,
    responseType: 'blob',
    headers: { 'Cache-Control': 'no-cache' },
  });
};

export const deleteUser = async (userId: string) => {
  return apiClient.delete(`/admin/v1/users/${userId}`);
};

export const restoreDeletedUser = async (userId: string) => {
  return apiClient.patch(`/admin/v1/users/${userId}/restore`);
};

export const inviteUserEmailLogin = async (userId: string, body: { email: string }) => {
  return apiClient.post<{ email: string; expiresAt: string }>(`/admin/v1/users/${userId}/email-login/invite`, body);
};

export const unlinkUserLoginMethod = async (userId: string, type: 'email' | 'kakao' | 'naver' | 'apple') => {
  return apiClient.delete<AdminUser>(`/admin/v1/users/${userId}/login-methods/${type}`);
};
